import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle, AlertCircle, Link2, Link2Off, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { quickbooksService, type QuickBooksStatus } from '@/services/quickbooks-service'
import { useAuthStore } from '@/store/auth-store'

export function QuickBooksSettings() {
  const { user } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN'

  const [status, setStatus] = useState<QuickBooksStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [flashMessage, setFlashMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Handle the ?qb= param that the OAuth callback redirects back with
  useEffect(() => {
    const qbParam = searchParams.get('qb')
    if (qbParam === 'connected') {
      setFlashMessage({ type: 'success', text: 'QuickBooks connected successfully!' })
      searchParams.delete('qb')
      setSearchParams(searchParams, { replace: true })
    } else if (qbParam === 'error') {
      setFlashMessage({ type: 'error', text: 'Could not connect to QuickBooks. Please try again.' })
      searchParams.delete('qb')
      setSearchParams(searchParams, { replace: true })
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [])

  // Auto-dismiss flash after 5s
  useEffect(() => {
    if (!flashMessage) return
    const t = setTimeout(() => setFlashMessage(null), 5000)
    return () => clearTimeout(t)
  }, [flashMessage])

  async function loadStatus() {
    setIsLoading(true)
    try {
      const s = await quickbooksService.getStatus()
      setStatus(s)
    } catch {
      // Non-fatal — just show as disconnected
      setStatus({ connected: false, realmId: null, tokenExpiresAt: null })
    } finally {
      setIsLoading(false)
    }
  }

  async function handleConnect() {
    try {
      const url = await quickbooksService.getConnectUrl()
      window.location.href = url
    } catch {
      setFlashMessage({ type: 'error', text: 'Could not initiate QuickBooks connection. Please try again.' })
    }
  }

  async function handleDisconnect() {
    if (!confirm('Disconnect QuickBooks? Future invoices will no longer sync automatically.')) return
    setIsDisconnecting(true)
    try {
      await quickbooksService.disconnect()
      setStatus({ connected: false, realmId: null, tokenExpiresAt: null })
      setFlashMessage({ type: 'success', text: 'QuickBooks disconnected.' })
    } catch {
      setFlashMessage({ type: 'error', text: 'Failed to disconnect. Please try again.' })
    } finally {
      setIsDisconnecting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Integrations</h3>
        <p className="text-sm text-gray-500">Connect third-party tools to sync data automatically.</p>
      </div>

      {/* Flash message */}
      {flashMessage && (
        <div
          className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium ${
            flashMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {flashMessage.type === 'success'
            ? <CheckCircle className="h-4 w-4 shrink-0" />
            : <AlertCircle className="h-4 w-4 shrink-0" />}
          {flashMessage.text}
        </div>
      )}

      {/* QuickBooks card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          {/* Logo + info */}
          <div className="flex items-start gap-4">
            {/* QB logo mark */}
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#2CA01C] text-white font-bold text-lg">
              QB
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-gray-900">QuickBooks Online</h4>
                {status?.connected && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Connected
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                When an invoice is created in Paver, it automatically syncs to your QuickBooks account
                as an invoice — including line items, totals, and customer details.
              </p>
              {status?.connected && status.realmId && (
                <p className="mt-2 text-xs text-gray-400">Company ID: {status.realmId}</p>
              )}
            </div>
          </div>

          {/* Action button */}
          <div className="shrink-0">
            {isLoading ? (
              <Button variant="outline" disabled>
                <RefreshCw className="h-4 w-4 animate-spin" />
              </Button>
            ) : status?.connected ? (
              isAdmin ? (
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                >
                  <Link2Off className="h-4 w-4 mr-2" />
                  {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              ) : (
                <span className="text-xs text-gray-400">Admin access required to disconnect</span>
              )
            ) : (
              isAdmin ? (
                <Button onClick={handleConnect} className="bg-[#2CA01C] hover:bg-[#238a16] text-white">
                  <Link2 className="h-4 w-4 mr-2" />
                  Connect QuickBooks
                </Button>
              ) : (
                <span className="text-xs text-gray-400">Admin access required to connect</span>
              )
            )}
          </div>
        </div>

        {/* What syncs */}
        {!status?.connected && (
          <div className="mt-5 border-t border-gray-100 pt-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">What syncs</p>
            <ul className="space-y-1">
              {[
                'Invoice created in Paver → Invoice created in QuickBooks',
                'Customer found or created automatically by email',
                'Line items from the bid totals (Asphalt Materials, Labor, etc.)',
                'Payment recorded in Paver → QuickBooks invoice marked paid',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
